import { useState, useEffect, useCallback } from 'react';

import { useNavigate } from 'react-router-dom';

import type { PlaybookDefinition, PlaybookDefinitionRequest, PlaybookType } from '@/api/configuration';
import {
  listPlaybookDefinitions,
  createPlaybookDefinition,
  updatePlaybookDefinition,
  deletePlaybookDefinition,
} from '@/api/configuration';
import { ApiError } from '@/modules/api/api-client';
import { useAuthStore } from '@/stores/auth-store';
import { useToastStore } from '@/stores/toast-store';
import {
  validateDefinitionForm,
} from '@/utils/playbook-definition-validation';
import type { DefinitionFormErrors } from '@/utils/playbook-definition-validation';

// --- Constants ---

const PLAYBOOK_TYPES: PlaybookType[] = [
  'DEPLOY',
  'CONFIGURATION',
  'RUN_DB_SCRIPT',
  'SERVICE_ACTION',
  'SERVICE_STATUS',
  'UTILITY',
  'SYSTEM_BUILD',
  'SERVER_UP',
  'REBOOT',
  'NONE',
];

const PLAYBOOK_TYPE_LABELS: Record<PlaybookType, string> = {
  DEPLOY: 'Deploy',
  CONFIGURATION: 'Configuration',
  RUN_DB_SCRIPT: 'Run DB Script',
  SERVICE_ACTION: 'Service Action',
  SERVICE_STATUS: 'Service Status',
  UTILITY: 'Utility',
  SYSTEM_BUILD: 'System Build',
  SERVER_UP: 'Server Up',
  REBOOT: 'Reboot',
  NONE: 'None',
};

// --- Page State ---

type FormMode = 'create' | 'edit' | null;
type FilterType = PlaybookType | 'ALL';
// --- Access Denied Component ---

function AccessDenied() {
  const navigate = useNavigate();

  return (
    <main id="main-content" className="max-w-4xl mx-auto px-4 py-6">
      <div className="text-center py-12">
        <svg
          className="mx-auto h-12 w-12 text-red-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
        <h1 className="mt-4 text-xl font-semibold text-content">Access Denied</h1>
        <p className="mt-2 text-sm text-content-secondary">
          You do not have permission to access the Playbook Definitions page. Only system administrators can manage definitions.
        </p>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="mt-6 inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 min-h-[44px]"
        >
          Go to Dashboard
        </button>
      </div>
    </main>
  );
}

// --- Confirmation Dialog ---

interface ConfirmDeleteDialogProps {
  definitionName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}

function ConfirmDeleteDialog({ definitionName, onConfirm, onCancel, isDeleting }: ConfirmDeleteDialogProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-delete-title"
    >
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} aria-hidden="true" />
      <div className="relative bg-surface rounded-lg shadow-xl p-6 max-w-md w-full mx-4 border border-border">
        <h2 id="confirm-delete-title" className="text-lg font-semibold text-content mb-2">
          Delete Definition
        </h2>
        <p className="text-sm text-content-secondary mb-6">
          Are you sure you want to delete <strong>{definitionName}</strong>? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium rounded-md text-content-secondary bg-surface border border-border hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 min-h-[44px]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 min-h-[44px]"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}
// --- Definition Form ---

interface DefinitionFormProps {
  mode: 'create' | 'edit';
  initial?: PlaybookDefinition;
  onSubmit: (data: PlaybookDefinitionRequest) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  serverErrors: DefinitionFormErrors;
}

function DefinitionForm({ mode, initial, onSubmit, onCancel, isSubmitting, serverErrors }: DefinitionFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [path, setPath] = useState(initial?.path ?? '');
  const [playbookType, setPlaybookType] = useState<string>(initial?.playbookType ?? '');
  const [errors, setErrors] = useState<DefinitionFormErrors>({});

  // Merge server errors into local errors for display
  const displayErrors: DefinitionFormErrors = { ...errors, ...serverErrors };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateDefinitionForm({
      name,
      path,
      description,
      playbookType,
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    onSubmit({
      name: name.trim(),
      description: description.trim() || null,
      path: path.trim(),
      playbookType: playbookType as PlaybookType,
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-lg border border-border bg-surface-raised p-6 space-y-4"
      aria-label={mode === 'create' ? 'Create definition form' : 'Edit definition form'}
    >
      <h2 className="text-lg font-semibold text-content mb-2">
        {mode === 'create' ? 'Add New Definition' : 'Edit Definition'}
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Name */}
        <div>
          <label htmlFor="def-name" className="block text-sm font-medium text-content-secondary mb-1">
            Name *
          </label>
          <input
            id="def-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. tomcat-deploy"
            aria-invalid={!!displayErrors.name}
            aria-describedby={displayErrors.name ? 'def-name-error' : undefined}
            className={`w-full rounded-md border px-3 py-2 text-sm text-content bg-surface focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 ${
              displayErrors.name ? 'border-red-500' : 'border-border'
            }`}
          />
          {displayErrors.name && (
            <p id="def-name-error" className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
              {displayErrors.name}
            </p>
          )}
        </div>

        {/* Playbook Type */}
        <div>
          <label htmlFor="def-type" className="block text-sm font-medium text-content-secondary mb-1">
            Playbook Type *
          </label>
          <select
            id="def-type"
            value={playbookType}
            onChange={(e) => setPlaybookType(e.target.value)}
            aria-invalid={!!displayErrors.playbookType}
            aria-describedby={displayErrors.playbookType ? 'def-type-error' : undefined}
            className={`w-full rounded-md border px-3 py-2 text-sm text-content bg-surface focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 ${
              displayErrors.playbookType ? 'border-red-500' : 'border-border'
            }`}
          >
            <option value="">Select a type...</option>
            {PLAYBOOK_TYPES.map((t) => (
              <option key={t} value={t}>{PLAYBOOK_TYPE_LABELS[t]}</option>
            ))}
          </select>
          {displayErrors.playbookType && (
            <p id="def-type-error" className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
              {displayErrors.playbookType}
            </p>
          )}
        </div>

        {/* Path */}
        <div className="sm:col-span-2">
          <label htmlFor="def-path" className="block text-sm font-medium text-content-secondary mb-1">
            Path *
          </label>
          <input
            id="def-path"
            type="text"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="e.g. deploy/tomcat-deploy.yml"
            aria-invalid={!!displayErrors.path}
            aria-describedby={displayErrors.path ? 'def-path-error' : undefined}
            className={`w-full rounded-md border px-3 py-2 text-sm text-content bg-surface focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 ${
              displayErrors.path ? 'border-red-500' : 'border-border'
            }`}
          />
          {displayErrors.path && (
            <p id="def-path-error" className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
              {displayErrors.path}
            </p>
          )}
        </div>

        {/* Description */}
        <div className="sm:col-span-2">
          <label htmlFor="def-description" className="block text-sm font-medium text-content-secondary mb-1">
            Description
            <span className="ml-1 text-xs text-content-tertiary font-normal">(optional)</span>
          </label>
          <textarea
            id="def-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Brief description of this playbook definition"
            aria-invalid={!!displayErrors.description}
            aria-describedby={displayErrors.description ? 'def-description-error' : undefined}
            className={`w-full rounded-md border px-3 py-2 text-sm text-content bg-surface focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 ${
              displayErrors.description ? 'border-red-500' : 'border-border'
            }`}
          />
          {displayErrors.description && (
            <p id="def-description-error" className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
              {displayErrors.description}
            </p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium rounded-md text-content-secondary bg-surface border border-border hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 min-h-[44px]"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 min-h-[44px]"
        >
          {isSubmitting
            ? mode === 'create' ? 'Creating...' : 'Saving...'
            : mode === 'create' ? 'Create Definition' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}
// --- Main Page ---

export function PlaybookDefinitionsPage() {
  const user = useAuthStore((state) => state.user);
  const addToast = useToastStore((state) => state.addToast);

  // Admin role check
  const isAdmin = user?.role === 'Admin';

  // Page state
  const [definitions, setDefinitions] = useState<PlaybookDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formMode, setFormMode] = useState<FormMode>(null);
  const [editingDefinition, setEditingDefinition] = useState<PlaybookDefinition | null>(null);
  const [filterType, setFilterType] = useState<FilterType>('ALL');
  const [deleteTarget, setDeleteTarget] = useState<PlaybookDefinition | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formErrors, setFormErrors] = useState<DefinitionFormErrors>({});

  // Data fetching
  const loadDefinitions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listPlaybookDefinitions();
      setDefinitions(data);
    } catch {
      setError('Failed to load playbook definitions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadDefinitions();
    }
  }, [isAdmin, loadDefinitions]);

  // Filtered definitions
  const filteredDefinitions = filterType === 'ALL'
    ? definitions
    : definitions.filter((d) => d.playbookType === filterType);

  // --- Handlers ---

  const handleOpenCreate = () => {
    setFormMode('create');
    setEditingDefinition(null);
    setFormErrors({});
  };

  const handleOpenEdit = (definition: PlaybookDefinition) => {
    setFormMode('edit');
    setEditingDefinition(definition);
    setFormErrors({});
  };

  const handleCloseForm = () => {
    setFormMode(null);
    setEditingDefinition(null);
    setFormErrors({});
  };

  const handleCreateSubmit = async (data: PlaybookDefinitionRequest) => {
    setIsSubmitting(true);
    setFormErrors({});
    try {
      await createPlaybookDefinition(data);
      addToast({ type: 'success', message: 'Definition created successfully.', duration: 5000 });
      handleCloseForm();
      loadDefinitions();
    } catch (err) {
      handleApiError(err, 'create');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (data: PlaybookDefinitionRequest) => {
    if (!editingDefinition) return;
    setIsSubmitting(true);
    setFormErrors({});
    try {
      await updatePlaybookDefinition(editingDefinition.id, data);
      addToast({ type: 'success', message: 'Definition updated successfully.', duration: 5000 });
      handleCloseForm();
      loadDefinitions();
    } catch (err) {
      handleApiError(err, 'update');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (definition: PlaybookDefinition) => {
    setDeleteTarget(definition);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deletePlaybookDefinition(deleteTarget.id);
      addToast({ type: 'success', message: 'Definition deleted successfully.', duration: 5000 });
      setDeleteTarget(null);
      loadDefinitions();
    } catch (err) {
      handleApiError(err, 'delete');
      setDeleteTarget(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
  };

  // --- Error handling ---

  const handleApiError = (err: unknown, operation: 'create' | 'update' | 'delete') => {
    if (err instanceof ApiError) {
      switch (err.status) {
        case 400: {
          const body = err.body as { message?: string; field?: string } | undefined;
          if (body?.field) {
            setFormErrors({ [body.field]: body.message } as DefinitionFormErrors);
          } else {
            addToast({ type: 'error', message: body?.message ?? 'Validation error', duration: 8000 });
          }
          break;
        }
        case 403:
          addToast({ type: 'error', message: "You don't have permission to perform this action.", duration: 8000 });
          break;
        case 404:
          addToast({ type: 'error', message: 'Definition not found. Refreshing list...', duration: 5000 });
          loadDefinitions();
          break;
        case 409:
          if (operation === 'delete') {
            addToast({ type: 'error', message: 'Cannot delete: this definition is still referenced by project playbook assignments.', duration: 8000 });
          } else {
            addToast({ type: 'error', message: 'A definition with this path already exists.', duration: 8000 });
          }
          break;
        default:
          addToast({ type: 'error', message: 'An unexpected error occurred.', duration: 8000 });
      }
    } else {
      addToast({ type: 'error', message: 'Network error. Please check your connection and try again.', duration: 8000 });
    }
  };

  // --- Access denied ---

  if (!isAdmin) {
    return <AccessDenied />;
  }
  return (
    <main id="main-content" className="max-w-4xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-content">Playbook Definitions</h1>
        {!formMode && (
          <button
            type="button"
            onClick={handleOpenCreate}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 min-h-[44px]"
          >
            <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Definition
          </button>
        )}
      </div>

      {/* Create/Edit Form */}
      {formMode && (
        <section className="mb-6" aria-labelledby="definition-form-heading">
          <DefinitionForm
            mode={formMode}
            initial={formMode === 'edit' && editingDefinition ? editingDefinition : undefined}
            onSubmit={formMode === 'create' ? handleCreateSubmit : handleEditSubmit}
            onCancel={handleCloseForm}
            isSubmitting={isSubmitting}
            serverErrors={formErrors}
          />
        </section>
      )}

      {/* Filter Control */}
      {!loading && !error && (
        <div className="flex items-center gap-3 mb-4">
          <label htmlFor="type-filter" className="text-sm font-medium text-content-secondary">
            Filter by type:
          </label>
          <select
            id="type-filter"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as FilterType)}
            className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
            aria-label="Filter definitions by playbook type"
          >
            <option value="ALL">All Types</option>
            {PLAYBOOK_TYPES.map((t) => (
              <option key={t} value={t}>{PLAYBOOK_TYPE_LABELS[t]}</option>
            ))}
          </select>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div role="alert" className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 mb-4">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          <button
            type="button"
            onClick={() => { setError(null); loadDefinitions(); }}
            className="mt-2 text-sm font-medium text-red-700 dark:text-red-300 underline focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 rounded"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading Skeleton */}
      {loading && (
        <div className="animate-pulse space-y-3" aria-label="Loading definitions">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-surface rounded-lg p-4 border border-border">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-2" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
            </div>
          ))}
        </div>
      )}

      {/* Definitions Table */}
      {!loading && !error && (
        <div aria-live="polite" aria-atomic="true">
          {filteredDefinitions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-content-secondary">
                {definitions.length === 0
                  ? 'No playbook definitions found. Add one to get started.'
                  : 'No definitions match the selected filter.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm" aria-label="Playbook definitions">
                <thead className="bg-surface-raised border-b border-border">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left font-medium text-content-secondary">Name</th>
                    <th scope="col" className="px-4 py-3 text-left font-medium text-content-secondary">Path</th>
                    <th scope="col" className="px-4 py-3 text-left font-medium text-content-secondary">Type</th>
                    <th scope="col" className="px-4 py-3 text-left font-medium text-content-secondary">Description</th>
                    <th scope="col" className="px-4 py-3 text-right font-medium text-content-secondary">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredDefinitions.map((def) => (
                    <tr key={def.id} className="hover:bg-surface-raised transition-colors">
                      <td className="px-4 py-3 font-medium text-content">{def.name}</td>
                      <td className="px-4 py-3 text-content-secondary font-mono text-xs">{def.path}</td>
                      <td className="px-4 py-3 text-content-secondary">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-brand-100 text-brand-800 dark:bg-brand-900/50 dark:text-brand-200">
                          {PLAYBOOK_TYPE_LABELS[def.playbookType]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-content-secondary max-w-xs truncate">
                        {def.description || '\u2014'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleOpenEdit(def)}
                            className="px-3 py-1.5 text-xs font-medium rounded-md text-content-secondary border border-border hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
                            aria-label={`Edit definition ${def.name}`}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(def)}
                            className="px-3 py-1.5 text-xs font-medium rounded-md text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-950 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                            aria-label={`Delete definition ${def.name}`}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteTarget && (
        <ConfirmDeleteDialog
          definitionName={deleteTarget.name}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          isDeleting={isDeleting}
        />
      )}
    </main>
  );
}